import { useState, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Upload, FileJson } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useFileUpload } from '@/hooks/useFileUpload';
import ConnectModal from '@/components/threatcat/ConnectModal';
import ProjectPicker from '@/components/threatcat/ProjectPicker';
import { useThreatCat } from '@/contexts/ThreatCatContext';
import { useProject } from '@/contexts/ProjectContext';
import { projectService } from '@/services/projectService';
import { queryKeys } from '@/lib/queryKeys';
import { threatcatService } from '@/services/threatcatService';
import { ThreatCatAuthError } from '@/lib/errors';
import { toast } from 'sonner';

export default function LandingPage() {
  const navigate = useNavigate();
  const { handleFile, error, isLoading } = useFileUpload();
  const [connectOpen, setConnectOpen] = useState(false);
  const [isAutoConnecting, setIsAutoConnecting] = useState(false);
  const { cookie, connect, disconnect } = useThreatCat();
  const { clearProject } = useProject();
  const queryClient = useQueryClient();
  const {
    data: localProjects = [],
    isLoading: localProjectsLoading,
    error: localProjectsError,
  } = useQuery({
    queryKey: queryKeys.projects.all,
    queryFn: projectService.list,
  });

  const handleConnectClick = useCallback(async () => {
    if (cookie) {
      setIsAutoConnecting(true);
      try {
        const projects = await threatcatService.fetchThreatCatProjects(cookie);
        await connect(cookie, projects);
        await queryClient.invalidateQueries({ queryKey: queryKeys.projects.all });
      } catch (error) {
        if (error instanceof ThreatCatAuthError) {
          disconnect();
          setConnectOpen(true);
        } else {
          toast.error(error instanceof Error ? error.message : 'Could not connect to ThreatCat.');
        }
      } finally {
        setIsAutoConnecting(false);
      }
    } else {
      setConnectOpen(true);
    }
  }, [cookie, connect, disconnect, queryClient]);

  const onDrop = useCallback(
    async (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      const files = e.dataTransfer.files;
      if (files.length > 0) {
        clearProject();
        const ok = await handleFile(files[0]);
        if (ok) {
          navigate('/local');
        }
      }
    },
    [clearProject, handleFile, navigate]
  );

  const onDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const onFileSelect = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (files && files.length > 0) {
        clearProject();
        const ok = await handleFile(files[0]);
        if (ok) {
          navigate('/local');
        }
      }
    },
    [clearProject, handleFile, navigate]
  );

  return (
    <div className="space-y-4">
      <ProjectPicker
        localProjects={localProjects}
        localLoading={localProjectsLoading || isAutoConnecting}
        localError={localProjectsError}
        onConnect={() => {
          if (!isAutoConnecting) {
            void handleConnectClick();
          }
        }}
        onDisconnect={disconnect}
      />

      <div className="container mx-auto max-w-6xl px-4 pb-8">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <FileJson className="h-5 w-5" />
              Review local export
            </CardTitle>
            <CardDescription>
              Review a ThreatCat JSON export without synchronizing it to the project catalog.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div
              onDrop={onDrop}
              onDragOver={onDragOver}
              className={cn(
                'flex items-center justify-between rounded-lg border-2 border-dashed p-4 transition-colors',
                'hover:border-primary/50 hover:bg-accent/50',
                isLoading && 'pointer-events-none opacity-50',
              )}
            >
              <div className="flex items-center gap-3">
                {isLoading ? (
                  <div className="h-6 w-6 animate-spin rounded-full border-4 border-primary border-t-transparent" />
                ) : (
                  <Upload className="h-6 w-6 text-muted-foreground" />
                )}
                <p className="text-sm text-muted-foreground">
                  {isLoading ? 'Processing...' : 'Drop a JSON export here or choose a file'}
                </p>
              </div>
              <input
                type="file"
                accept=".json"
                onChange={onFileSelect}
                className="hidden"
                id="file-upload"
                disabled={isLoading}
              />
              <Button asChild variant="outline" size="sm" disabled={isLoading}>
                <label htmlFor="file-upload" className="cursor-pointer">
                  Choose file
                </label>
              </Button>
            </div>

            {error && (
              <div className="rounded-md border border-destructive/50 bg-destructive/10 p-3">
                <p className="text-xs text-destructive">{error.message}</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <ConnectModal
        open={connectOpen}
        onClose={() => setConnectOpen(false)}
        onConnected={() => {
          void queryClient.invalidateQueries({ queryKey: queryKeys.projects.all });
        }}
      />
    </div>
  );
}
